import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { EASE, MaskLine } from "./motion";
import { LogoMark } from "./Logo";
import { IMAGES, LINKS } from "@/lib/site";
import { scrollToId } from "@/lib/lenis";
import { CountdownChip } from "./Countdown";

const WORD = ["S", "F", "U", "M", "O"];

export const Equalizer = ({ className = "h-3.5" }) => (
  <span className={`flex items-end gap-[3px] ${className}`} aria-hidden>
    {[0, 0.3, 0.15, 0.45].map((d) => (
      <span key={d} className="eq-bar block h-full w-[3px] bg-acid" style={{ animationDelay: `${d}s` }} />
    ))}
  </span>
);

const Sticker = () => (
  <div className="relative h-28 w-28 rounded-full bg-acid text-ink shadow-[0_20px_60px_-10px_rgba(212,255,58,0.45)] sm:h-32 sm:w-32">
    <svg viewBox="0 0 200 200" className="spin-slow absolute inset-0 h-full w-full">
      <defs>
        <path id="sfu-sticker-circle" d="M100,100 m-72,0 a72,72 0 1,1 144,0 a72,72 0 1,1 -144,0" />
      </defs>
      <text fontFamily="JetBrains Mono" fontSize="14" letterSpacing="3.2" fill="#0A0A0B" fontWeight="500">
        <textPath href="#sfu-sticker-circle">SFUSHOP • DROP 01 • SFUSHOP • DROP 01 • </textPath>
      </text>
    </svg>
    <span className="absolute inset-0 flex items-center justify-center">
      <LogoMark className="h-10 w-10" color="#0A0A0B" dot={false} />
    </span>
  </div>
);

const HeroPortrait = ({ imgY, rotateX, rotateY }) => (
  <motion.div style={{ y: imgY }} className="relative z-10 mx-auto mt-14 aspect-[4/5] w-[86%] sm:w-2/3 lg:absolute lg:right-[5vw] lg:top-[30vh] lg:mt-0 lg:w-[25vw] lg:max-w-[470px]">
    <div className="h-full w-full" style={{ perspective: 1200 }}>
      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative h-full w-full">
        <motion.div
          initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
          transition={{ duration: 1.6, delay: 0.55, ease: EASE }}
          className="clip-frame relative h-full w-full overflow-hidden bg-ink-3"
        >
          <motion.img
            src={IMAGES.hero}
            alt="SFUMO ritratto"
            initial={{ scale: 1.35 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, delay: 0.55, ease: EASE }}
            className="h-full w-full object-cover object-[55%_center] grayscale-[35%] contrast-[1.1]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/10" />
          <div className="absolute left-4 top-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-bone">
            <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> Rec — Studio / Notte
          </div>
          <div className="absolute bottom-5 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/80">SFUMO © 2026</div>
        </motion.div>
        <motion.div
          initial={{ scale: 0, rotate: -90 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ duration: 1.1, delay: 1.5, ease: EASE }}
          style={{ transform: "translateZ(80px)" }}
          className="absolute -bottom-10 -left-8 sm:-left-14"
        >
          <Sticker />
        </motion.div>
      </motion.div>
    </div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const wordY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 80, damping: 18 });
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative isolate min-h-[100svh] overflow-hidden px-5 pb-20 pt-24 sm:px-8 lg:px-12">
      <div aria-hidden className="pointer-events-none absolute -left-40 top-1/3 -z-10 h-[560px] w-[560px] rounded-full bg-acid/[0.07] blur-[140px]" />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 1 }}
        className="relative z-30 flex items-center justify-between gap-3 border-b border-line pb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke sm:text-[11px]"
      >
        <span>(01) Artista pop indipendente</span>
        <button onClick={() => scrollToId("app")} data-testid="hero-countdown-chip" className="hidden items-center gap-2 uppercase tracking-[0.25em] transition-colors hover:text-acid lg:flex">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-acid" /> InkMaster, l'app tattoo — tra <CountdownChip />
        </button>
        <span className="flex items-center gap-2 text-bone">
          <Equalizer /> <span className="hidden sm:inline">Ascoltami su Spotify</span>
        </span>
      </motion.div>

      <motion.h1
        style={{ y: wordY }}
        data-testid="hero-title"
        aria-label="SFUMO"
        className="relative z-20 mt-3 flex select-none justify-between font-display text-[20vw] font-black leading-[0.9] tracking-[-0.04em] text-bone mix-blend-difference"
      >
        {WORD.map((ch, i) => (
          <span key={ch} className="block overflow-hidden">
            <motion.span
              className="block"
              initial={{ y: "105%", filter: "blur(18px)" }}
              animate={{ y: "0%", filter: "blur(0px)" }}
              transition={{ duration: 1.5, delay: 0.1 + i * 0.08, ease: EASE }}
            >
              {ch}
            </motion.span>
          </span>
        ))}
      </motion.h1>

      <div className="relative z-30 mt-6 max-w-2xl lg:mt-8 lg:max-w-[46vw]">
        <h2 className="font-display text-3xl font-bold uppercase leading-[1] tracking-tight sm:text-4xl xl:text-[2.9rem]">
          <MaskLine onLoad delay={0.75}>Musica senza filtri.</MaskLine>
          <MaskLine onLoad delay={0.88} className="text-outline">Stile senza regole.</MaskLine>
        </h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: EASE }}
          className="mt-5 max-w-lg text-base leading-relaxed text-bone/70"
        >
          Sono SFUMO: scrivo di notte, registro in cameretta e pubblico senza chiedere il permesso a nessuno. Qui trovi la mia musica e{" "}
          <span className="font-semibold text-acid">sfushop</span>, il brand che veste le mie canzoni.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease: EASE }}
          className="mt-7 flex flex-wrap items-center gap-3"
        >
          <a
            href={LINKS.spotify}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-spotify-button"
            className="group inline-flex h-12 items-center gap-3 rounded-full bg-acid pl-5 pr-6 font-mono text-xs font-medium uppercase tracking-[0.18em] text-ink transition-transform duration-300 hover:-translate-y-0.5 active:scale-95"
          >
            <i className="fa-brands fa-spotify text-lg transition-transform duration-500 group-hover:rotate-[360deg]" /> Ascolta su Spotify
          </a>
          <button
            onClick={() => scrollToId("sfushop")}
            data-testid="hero-sfushop-button"
            className="group inline-flex h-12 items-center gap-3 rounded-full border border-bone/30 px-6 font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-300 hover:border-acid hover:text-acid"
          >
            Entra in sfushop <ArrowDownRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-[-45deg]" />
          </button>
        </motion.div>
      </div>

      <HeroPortrait imgY={imgY} rotateX={rotateX} rotateY={rotateY} />
    </section>
  );
};
